import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';
import { PortfolioEnterpriseGroup, StringFilter } from '@mews-commander/data-access';
import { LocalizedText, lt, showSuccessAlert, useDialogAction } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    Stack,
    Typography,
    useTableState,
    TableProvider,
    TableHeader,
    Table,
    IconButton,
    IconName,
} from '@optimus-web/core';
import { EnterpriseGroupWithRatesCount } from '../types';
import { getRatesCountPerPropertyGroup } from '../utils';
import { ACTIONS_COLUMN_WIDTH, RATE_COUNT_COLUMN_WIDTH } from './PropertiesTable';
import { useUnassignProperties } from './queries';
import { UnassignPropertiesDialog } from './PropertiesTab/UnassignPropertiesDialog';

const propertyGroupsTableColumnHelper = createColumnHelper<EnterpriseGroupWithRatesCount>();
type PropertyGroupKeys = keyof Pick<EnterpriseGroupWithRatesCount, 'name' | 'ratesCount'>;
type PropertyGroupFilters = Partial<Record<PropertyGroupKeys, Array<StringFilter> | null>>;

const propertyGroupsTableColumns = (onDeleteClick: (propertyGroupId: string) => void) => [
    propertyGroupsTableColumnHelper.accessor('name', {
        header: () => <LocalizedText textKey={TextKey.PropertyGroup} />,
        cell: (props) => (
            <Stack vertical>
                <Typography textStyle="bodyMediumStrong" as="div">
                    {props.getValue()}
                </Typography>
                <Typography textStyle="bodyMedium" as="div">
                    <LocalizedText
                        textKey={TextKey.PropertiesNumber}
                        substitutes={{ count: props.row.original.propertyIds.length }}
                    />
                </Typography>
            </Stack>
        ),
    }),
    propertyGroupsTableColumnHelper.accessor('ratesCount', {
        header: () => <LocalizedText textKey={TextKey.RatePlural} />,
        meta: { width: RATE_COUNT_COLUMN_WIDTH },
        cell: (props) => props.getValue() ?? 0,
    }),
    propertyGroupsTableColumnHelper.display({
        id: 'actions',
        meta: { width: ACTIONS_COLUMN_WIDTH, align: 'end' },
        cell: (props) => (
            <IconButton
                icon={IconName.Unlock}
                onClick={() => onDeleteClick(props.row.original.id)}
                size="small"
                variant="ghost"
                type="submit"
            />
        ),
    }),
];

interface PropertyGroupsTableProps {
    propertyGroupsCount: number;
    assignedEnterpriseGroupIds?: string[] | null;
    propertyGroups: PortfolioEnterpriseGroup[];
    ratesCountPerPropertyGroup: ReturnType<typeof getRatesCountPerPropertyGroup>;
    centralRateGroupId: string;
}

export const PropertyGroupsTable = ({
    propertyGroups,
    propertyGroupsCount,
    assignedEnterpriseGroupIds,
    ratesCountPerPropertyGroup,
    centralRateGroupId,
}: PropertyGroupsTableProps) => {
    const [propertyGroupIdToUnassign, setPropertyGroupIdToUnassign] = useState<string | null>(null);
    const { mutateAsync } = useUnassignProperties();
    const [tableState, setTableState] = useTableState<EnterpriseGroupWithRatesCount, PropertyGroupFilters>({
        pagination: {
            show: false,
        },
    });

    const propertyGroupsTableData = assignedEnterpriseGroupIds?.map((id) => ({
        ...propertyGroups.find((propertyGroup) => propertyGroup.id === id),
        ratesCount: ratesCountPerPropertyGroup?.[id] ?? 0,
    }));

    const unassignPropertyGroupAction = useDialogAction(async () => {
        if (!propertyGroupIdToUnassign) {
            return null;
        }

        await mutateAsync(
            { centralRateGroupId, enterpriseGroupIds: [propertyGroupIdToUnassign] },
            {
                onSuccess: () => {
                    showSuccessAlert({
                        title: lt('PropertyGroupUnassigned'),
                    });
                },
            }
        );
    });

    const handleModalOpen = (propertyGroupId: string) => {
        setPropertyGroupIdToUnassign(propertyGroupId);
        unassignPropertyGroupAction.handlers.handleOpen();
    };

    const columns = propertyGroupsTableColumns(handleModalOpen);

    if (!propertyGroupsTableData) {
        return null;
    }

    return (
        <TableProvider columns={columns} state={tableState} onStateChange={setTableState}>
            <TableHeader
                title={<LocalizedText textKey={TextKey.PropertyGroupsNavigation} />}
                subTitle={propertyGroupsCount}
            />
            <Table
                data={propertyGroupsTableData}
                amountOfRows={propertyGroupsCount}
                uniqueRowId="id"
                fixedFirstColumn
            />
            <UnassignPropertiesDialog
                unassignPropertyAction={unassignPropertyGroupAction}
                active={unassignPropertyGroupAction.states.isDialogOpened}
                titleTextKey={TextKey.PropertyGroupUnassignDialogTitle}
                descriptionTextKey={TextKey.PropertyGroupUnassignDialogDescription}
            />
        </TableProvider>
    );
};
